import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { s360ConfigService } from "../Simplify360Chat/Services/S360Config";

const getTrustedParentOrigin = () => {
  const parentUrl = s360ConfigService.sourceOrigin || document.referrer;
  if (!parentUrl) {
    return null;
  }

  try {
    return new URL(parentUrl).origin;
  } catch {
    return null;
  }
};

const NetworkStatusContext = createContext({
  isOnline: true,
  networkQuality: "good",
  lastChecked: null,
  connectionType: null,
  latency: null,
  updateNetworkStatus: () => {},
});

export const NetworkStatusProvider = ({ children }) => {
  const [networkStatus, setNetworkStatus] = useState({
    isOnline: true,
    networkQuality: "good",
    lastChecked: new Date().toISOString(),
    connectionType: null,
    latency: null,
  });

  // Handler for parent window messages
  const handleNetworkStatusMessage = useCallback((event) => {
    const trustedParentOrigin = getTrustedParentOrigin();

    if (
      !trustedParentOrigin ||
      event.source !== window.parent ||
      event.origin !== trustedParentOrigin
    ) {
      return;
    }

    // Validate the message type
    // if (event.data && event.data.type) {
    // }
    if (event.data && event.data.type === "network-status-update") {
      setNetworkStatus((prevStatus) => {
        const newStatus = {
          isOnline: event.data.isOnline,
          networkQuality: event.data.networkQuality || "good",
          lastChecked: event.data.timestamp || new Date().toISOString(),
          connectionType: event.data.connectionType,
          latency: event.data.latency,
        };

        // Log if status actually changed
        if (prevStatus.isOnline !== newStatus.isOnline) {
        }

        return newStatus;
      });
    }
  }, []);

  useEffect(() => {
    // Listen for messages from parent
    window.addEventListener("message", handleNetworkStatusMessage);

    // Request initial status from parent and periodically
    const requestStatus = () => {
      const trustedParentOrigin = getTrustedParentOrigin();
      if (
        trustedParentOrigin &&
        window.parent &&
        window.parent !== window
      ) {
        window.parent.postMessage(
          { type: "request-network-status" },
          trustedParentOrigin
        );
      }
    };

    // Request immediately
    requestStatus();

    // Request every 10 seconds as a fallback
    const intervalId = setInterval(requestStatus, 10000);

    return () => {
      window.removeEventListener("message", handleNetworkStatusMessage);
      clearInterval(intervalId);
    };
  }, [handleNetworkStatusMessage]);
  // Manual update function for direct updates
  const updateNetworkStatus = useCallback((status) => {
    setNetworkStatus((prev) => ({
      ...prev,
      ...status,
      lastChecked: new Date().toISOString(),
    }));
  }, []);

  return (
    <NetworkStatusContext.Provider
      value={{ ...networkStatus, updateNetworkStatus }}
    >
      {children}
    </NetworkStatusContext.Provider>
  );
};

export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  if (!context) {
    throw new Error(
      "useNetworkStatus must be used within NetworkStatusProvider"
    );
  }
  return context;
};
